import { shade, transparentize } from 'polished'
import { baseBorderRadii } from './base'
import { luxuryEscapesDefaultPalette as legacyDefaultPalette, luxuryEscapesInversePalette as legacyInversePalette, luxuryEscapesTheme } from './luxuryescapes'
import { neutralControlPalette, neutralScrimPalette } from './neutralOverlayPalettes'
import { BLACK, BLUE, BRONZE, BROWN, CORAL, GOLD, GREY, INFINITE, LIME_GREEN, OCEAN, ORANGE, PLATINUM, PURPLE, RED, SAND, TEAL } from './primitiveColours'
import { baseThemeShadows } from './shadows'
import { CJK_SANS_FONTS, CJK_SERIF_FONTS } from 'constants/cjkFonts'

const neutralPalette: Record<App.Design.PaletteType, App.Design.NeutralPalette<string>> = {
  default: {
    default: {
      one: GREY[800],
      two: GREY[700],
      three: GREY[500],
      four: GREY[300],
      five: GREY[200],
      six: GREY[100],
      seven: GREY[50],
      eight: GREY[0],
      clear: transparentize(0.84, GREY[0]),
    },
    surface: {
      normalForeground: BLACK[0],
      normalBackground: BLACK[0],
      normalContrast: BLACK[900],
      lightForeground: SAND[200],
      lightBackground: SAND[50],
      lightContrast: BLACK[900],
      lightForegroundAlt: BLACK[700],
      lightBackgroundAlt: SAND[100],
      lightContrastAlt: BLACK[900],
      darkForeground: BLACK[900],
      darkBackground: BLACK[900],
      darkContrast: BLACK[0],
      staticWhite: BLACK[0],
    },
    control: neutralControlPalette.default,
    scrim: neutralScrimPalette,
  },
  inverse: {
    default: {
      one: GREY[0],
      two: GREY[50],
      three: GREY[100],
      four: GREY[200],
      five: GREY[300],
      six: GREY[500],
      seven: GREY[700],
      eight: GREY[800],
      clear: transparentize(0.84, GREY[0]),
    },
    surface: {
      normalForeground: BLACK[900],
      normalBackground: BLACK[900],
      normalContrast: BLACK[0],
      lightForeground: BLACK[700],
      lightBackground: BLACK[800],
      lightContrast: BLACK[0],
      lightForegroundAlt: BLACK[200],
      lightBackgroundAlt: BLACK[700],
      lightContrastAlt: BLACK[0],
      darkForeground: BLACK[0],
      darkBackground: BLACK[0],
      darkContrast: BLACK[900],
      staticWhite: BLACK[0],
    },
    control: neutralControlPalette.inverse,
    scrim: neutralScrimPalette,
  },
}

const productPalette: Record<App.Design.PaletteType, App.Design.ProductPalette<string>> = {
  default: {
    exclusive: {
      foreground: GOLD[600],
      background: GOLD[600],
      contrast: GREY[0],
    },
    escape: {
      foreground: RED[600],
      background: RED[500],
      contrast: GREY[0],
    },
    partner: {
      foreground: GREY[600],
      background: GREY[600],
      contrast: GREY[0],
    },
    premium: {
      foreground: GREY[700],
      background: GREY[700],
      contrast: GREY[0],
    },
    ultralux: {
      foreground: GOLD[600],
      background: GOLD[600],
      contrast: GREY[0],
      active: shade(0.24, GOLD[600]),
      hover: shade(0.16, GOLD[600]),
    },
    luxPlus: {
      foreground: BLUE[700],
      altForeground: BLUE[700],
      background: BLUE[700],
      contrast: GREY[0],
      active: shade(0.24, BLUE[700]),
      altActive: shade(0.24, BLUE[700]),
      hover: shade(0.16, BLUE[700]),
      altHover: shade(0.16, BLUE[700]),
    },
    ai: {
      gradient: `linear-gradient(180deg, ${TEAL[100]} 0%, ${TEAL[600]} 100%)`,
      light: TEAL[100],
      light35: transparentize(0.65, TEAL[100]),
      dark: TEAL[600],
      dark25: transparentize(0.75, TEAL[600]),
      contrast: GREY[800],
    },
    loyalty: {
      bronze: {
        gradientPrimaryBackground: `linear-gradient(45deg, ${BRONZE[300]} 0.73%, ${BRONZE[100]} 45.77%, ${BRONZE[300]} 74.49%)`,
        gradientSecondaryBackground: `linear-gradient(270deg, ${BRONZE[100]} 0%, ${BRONZE[300]} 100%)`,
        gradientTertiaryBackground: `linear-gradient(45deg, ${BRONZE[800]} 0.73%, ${BRONZE[800]} 45.77%, ${BRONZE[800]} 74.49%)`,
        gradientTertiaryContrast: BRONZE[50],
        darkBackground: BRONZE[600],
        darkContrast: BLACK[0],
        darkForeground: BRONZE[600],
        lightBackground: BRONZE[100],
        lightContrast: BLACK[900],
        lightForeground: BRONZE[100],
        normalBackground: BRONZE[300],
        normalContrast: BLACK[900],
        normalForeground: BRONZE[300],
      },
      silver: {
        gradientPrimaryBackground: `linear-gradient(45deg, ${GREY[300]} 0.73%, ${GREY[100]} 45.77%, ${GREY[300]} 74.49%)`,
        gradientSecondaryBackground: `linear-gradient(270deg, ${GREY[100]} 0%, ${GREY[300]} 100%)`,
        gradientTertiaryBackground: `linear-gradient(45deg, ${GREY[800]} 0.73%, ${GREY[800]} 45.77%, ${GREY[800]} 74.49%)`,
        gradientTertiaryContrast: GREY[50],
        darkBackground: GREY[600],
        darkContrast: BLACK[0],
        darkForeground: GREY[600],
        lightBackground: GREY[100],
        lightContrast: BLACK[900],
        lightForeground: GREY[100],
        normalBackground: GREY[300],
        normalContrast: BLACK[900],
        normalForeground: GREY[300],
      },
      gold: {
        gradientPrimaryBackground: `linear-gradient(45deg, ${GOLD[300]} 0.73%, ${GOLD[100]} 45.77%, ${GOLD[300]} 74.49%)`,
        gradientSecondaryBackground: `linear-gradient(270deg, ${GOLD[100]} 0%, ${GOLD[300]} 100%)`,
        gradientTertiaryBackground: `linear-gradient(45deg, ${GOLD[800]} 0.73%, ${GOLD[800]} 45.77%, ${GOLD[800]} 74.49%)`,
        gradientTertiaryContrast: GOLD[50],
        darkBackground: GOLD[600],
        darkContrast: BLACK[0],
        darkForeground: GOLD[600],
        lightBackground: GOLD[100],
        lightContrast: BLACK[900],
        lightForeground: GOLD[100],
        normalBackground: GOLD[300],
        normalContrast: BLACK[900],
        normalForeground: GOLD[300],
      },
      platinum: {
        gradientPrimaryBackground: `linear-gradient(45deg, ${PLATINUM[800]} 0.73%, ${PLATINUM[600]} 45.77%, ${PLATINUM[800]} 74.49%)`,
        gradientSecondaryBackground: `linear-gradient(270deg, ${PLATINUM[600]} 0%, ${PLATINUM[800]} 100%)`,
        gradientTertiaryBackground: `linear-gradient(45deg, ${GREY[50]} 0.73%, ${GREY[50]} 45.77%, ${GREY[50]} 74.49%)`,
        gradientTertiaryContrast: PLATINUM[900],
        normalForeground: PLATINUM[600],
        normalBackground: PLATINUM[600],
        normalContrast: BLACK[0],
        lightForeground: PLATINUM[200],
        lightBackground: PLATINUM[200],
        lightContrast: BLACK[900],
        darkForeground: PLATINUM[800],
        darkBackground: PLATINUM[800],
        darkContrast: BLACK[0],
      },
      infinite: {
        gradientPrimaryBackground: `linear-gradient(45deg, ${INFINITE[950]} 0.73%, ${INFINITE[700]} 45.77%, ${INFINITE[950]} 74.49%)`,
        gradientSecondaryBackground: `linear-gradient(270deg, ${INFINITE[700]} 0%, ${INFINITE[950]} 100%)`,
        gradientTertiaryBackground: `linear-gradient(45deg, ${INFINITE[300]} 0.73%, ${INFINITE[100]} 45.77%, ${INFINITE[300]} 74.49%)`,
        gradientTertiaryContrast: INFINITE[800],
        normalForeground: INFINITE[600],
        normalBackground: INFINITE[800],
        normalContrast: BLACK[0],
        lightForeground: INFINITE[600],
        lightBackground: INFINITE[800],
        lightContrast: BLACK[0],
        darkForeground: INFINITE[600],
        darkBackground: INFINITE[800],
        darkContrast: BLACK[0],
      },
    },
    flights: {
      seats: {
        standard: {
          normalForeground: ORANGE[500],
          normalBackground: ORANGE[500],
          normalContrast: GREY[800],
          lightForeground: ORANGE[50],
          lightBackground: ORANGE[50],
          lightContrast: ORANGE[800],
        },
        upfront: {
          normalForeground: BLUE[500],
          normalBackground: BLUE[500],
          normalContrast: GREY[0],
          lightForeground: BLUE[50],
          lightBackground: BLUE[50],
          lightContrast: BLUE[700],
        },
        legroom: {
          normalForeground: PURPLE[500],
          normalBackground: PURPLE[500],
          normalContrast: GREY[0],
          lightForeground: PURPLE[50],
          lightBackground: PURPLE[50],
          lightContrast: PURPLE[700],
        },
      },
    },
  },
  inverse: {
    exclusive: {
      foreground: GOLD[300],
      background: GOLD[500],
      contrast: GREY[0],
    },
    escape: {
      foreground: RED[200],
      background: RED[500],
      contrast: GREY[0],
    },
    partner: {
      foreground: GREY[200],
      background: GREY[500],
      contrast: GREY[0],
    },
    premium: {
      foreground: GREY[300],
      background: GREY[700],
      contrast: GREY[0],
    },
    ultralux: {
      foreground: GOLD[300],
      background: GOLD[500],
      contrast: GREY[0],
      active: shade(0.24, GOLD[500]),
      hover: shade(0.16, GOLD[500]),
    },
    luxPlus: {
      foreground: BLUE[300],
      altForeground: GREY[0],
      background: BLUE[700],
      contrast: GREY[0],
      active: shade(0.24, BLUE[700]),
      altActive: shade(0.24, GREY[0]),
      hover: shade(0.16, BLUE[700]),
      altHover: shade(0.16, GREY[0]),
    },
    ai: {
      gradient: `linear-gradient(180deg, ${TEAL[100]} 0%, ${TEAL[600]} 100%)`,
      light: TEAL[100],
      light35: transparentize(0.65, TEAL[100]),
      dark: TEAL[600],
      dark25: transparentize(0.75, TEAL[600]),
      contrast: GREY[0],
    },
    loyalty: {
      bronze: {
        gradientPrimaryBackground: `linear-gradient(45deg, ${BRONZE[400]} 0.73%, ${BRONZE[100]} 45.77%, ${BRONZE[400]} 74.49%)`,
        gradientSecondaryBackground: `linear-gradient(270deg, ${BRONZE[400]} 0, ${BRONZE[100]} 100%)`,
        gradientTertiaryBackground: `linear-gradient(45deg, ${BRONZE[800]} 0.73%, ${BRONZE[800]} 45.77%, ${BRONZE[800]} 74.49%)`,
        gradientTertiaryContrast: BLACK[0],
        darkBackground: BRONZE[600],
        darkContrast: BLACK[0],
        darkForeground: BRONZE[400],
        lightBackground: BRONZE[900],
        lightContrast: BLACK[0],
        lightForeground: BRONZE[900],
        normalBackground: BRONZE[300],
        normalContrast: BLACK[900],
        normalForeground: BRONZE[300],
      },
      silver: {
        gradientPrimaryBackground: `linear-gradient(45deg, ${GREY[300]} 0.73%, ${GREY[100]} 45.77%, ${GREY[300]} 74.49%)`,
        gradientSecondaryBackground: `linear-gradient(270deg, ${GREY[300]} 0, ${GREY[100]} 100%)`,
        gradientTertiaryBackground: `linear-gradient(45deg, ${GREY[800]} 0.73%, ${GREY[800]} 45.77%, ${GREY[800]} 74.49%)`,
        gradientTertiaryContrast: BLACK[0],
        darkBackground: GREY[600],
        darkContrast: BLACK[0],
        darkForeground: GREY[400],
        lightBackground: GREY[900],
        lightContrast: BLACK[0],
        lightForeground: GREY[900],
        normalBackground: GREY[300],
        normalContrast: BLACK[900],
        normalForeground: GREY[300],
      },
      gold: {
        gradientPrimaryBackground: `linear-gradient(45deg, ${GOLD[300]} 0.73%, ${GOLD[100]} 45.77%, ${GOLD[300]} 74.49%)`,
        gradientSecondaryBackground: `linear-gradient(270deg, ${GOLD[300]} 0, ${GOLD[100]} 100%)`,
        gradientTertiaryBackground: `linear-gradient(45deg, ${GOLD[800]} 0.73%, ${GOLD[800]} 45.77%, ${GOLD[800]} 74.49%)`,
        gradientTertiaryContrast: BLACK[0],
        darkBackground: GOLD[600],
        darkContrast: BLACK[0],
        darkForeground: GOLD[400],
        lightBackground: GOLD[900],
        lightContrast: BLACK[0],
        lightForeground: GOLD[900],
        normalBackground: GOLD[300],
        normalContrast: BLACK[900],
        normalForeground: GOLD[300],
      },
      platinum: {
        gradientPrimaryBackground: `linear-gradient(45deg, ${PLATINUM[900]} 0.73%, ${PLATINUM[600]} 45.77%, ${PLATINUM[900]} 74.49%)`,
        gradientSecondaryBackground: `linear-gradient(270deg, ${PLATINUM[600]} 0, ${PLATINUM[900]} 100%)`,
        gradientTertiaryBackground: `linear-gradient(45deg, ${GREY[50]} 0.73%, ${GREY[50]} 45.77%, ${GREY[50]} 74.49%)`,
        gradientTertiaryContrast: BLACK[900],
        normalForeground: PLATINUM[600],
        normalBackground: PLATINUM[600],
        normalContrast: BLACK[0],
        lightForeground: PLATINUM[900],
        lightBackground: PLATINUM[900],
        lightContrast: BLACK[0],
        darkForeground: PLATINUM[400],
        darkBackground: PLATINUM[800],
        darkContrast: BLACK[0],
      },
      infinite: {
        gradientPrimaryBackground: `linear-gradient(45deg, ${INFINITE[950]} 0.73%, ${INFINITE[700]} 45.77%, ${INFINITE[950]} 74.49%)`,
        gradientSecondaryBackground: `linear-gradient(270deg, ${INFINITE[950]} 0%, ${INFINITE[700]} 100%)`,
        gradientTertiaryBackground: `linear-gradient(45deg, ${INFINITE[300]} 0.73%, ${INFINITE[100]} 45.77%, ${INFINITE[300]} 74.49%)`,
        gradientTertiaryContrast: INFINITE[800],
        normalForeground: INFINITE[500],
        normalBackground: INFINITE[800],
        normalContrast: BLACK[0],
        lightForeground: INFINITE[500],
        lightBackground: INFINITE[900],
        lightContrast: BLACK[0],
        darkForeground: INFINITE[500],
        darkBackground: INFINITE[800],
        darkContrast: BLACK[0],
      },
    },
    flights: {
      seats: {
        standard: {
          normalForeground: ORANGE[500],
          normalBackground: ORANGE[500],
          normalContrast: GREY[800],
          lightForeground: ORANGE[900],
          lightBackground: ORANGE[900],
          lightContrast: ORANGE[50],
        },
        upfront: {
          normalForeground: BLUE[500],
          normalBackground: BLUE[500],
          normalContrast: GREY[0],
          lightForeground: BLUE[900],
          lightBackground: BLUE[900],
          lightContrast: BLUE[100],
        },
        legroom: {
          normalForeground: PURPLE[500],
          normalBackground: PURPLE[500],
          normalContrast: GREY[0],
          lightForeground: PURPLE[900],
          lightBackground: PURPLE[900],
          lightContrast: PURPLE[50],
        },
      },
    },
  },
}

const messagingPalette: Record<App.Design.PaletteType, App.Design.MessagingPalette<string>> = {
  default: {
    success: {
      normalForeground: LIME_GREEN[500],
      normalBackground: LIME_GREEN[500],
      normalContrast: BLACK[900],
      lightForeground: LIME_GREEN[50],
      lightBackground: LIME_GREEN[50],
      lightContrast: BLACK[900],
      darkForeground: LIME_GREEN[700],
      darkBackground: LIME_GREEN[700],
      darkContrast: BLACK[0],
    },
    warning: {
      normalForeground: ORANGE[500],
      normalBackground: ORANGE[500],
      normalContrast: BLACK[900],
      lightForeground: ORANGE[100],
      lightBackground: ORANGE[100],
      lightContrast: BLACK[900],
      darkForeground: ORANGE[700],
      darkBackground: ORANGE[700],
      darkContrast: BLACK[0],
    },
    urgency: {
      normalForeground: BROWN[500],
      normalBackground: BROWN[500],
      normalContrast: BLACK[0],
      lightForeground: BROWN[50],
      lightBackground: BROWN[50],
      lightContrast: BLACK[900],
      darkForeground: BROWN[800],
      darkBackground: BROWN[800],
      darkContrast: BLACK[0],
    },
    critical: {
      normalForeground: RED[600],
      normalBackground: RED[600],
      normalContrast: BLACK[0],
      lightForeground: RED[200],
      lightBackground: RED[200],
      lightContrast: BLACK[900],
      darkForeground: RED[700],
      darkBackground: RED[700],
      darkContrast: BLACK[0],
    },
    favourite: {
      normalForeground: RED[600],
      normalBackground: RED[600],
      normalContrast: BLACK[0],
      lightForeground: RED[200],
      lightBackground: RED[200],
      lightContrast: BLACK[900],
      darkForeground: RED[700],
      darkBackground: RED[700],
      darkContrast: BLACK[0],
    },
  },
  inverse: {
    success: {
      normalForeground: LIME_GREEN[500],
      normalBackground: LIME_GREEN[500],
      normalContrast: BLACK[900],
      lightForeground: LIME_GREEN[900],
      lightBackground: LIME_GREEN[900],
      lightContrast: BLACK[0],
      darkForeground: LIME_GREEN[300],
      darkBackground: LIME_GREEN[300],
      darkContrast: BLACK[900],
    },
    warning: {
      normalForeground: ORANGE[500],
      normalBackground: ORANGE[500],
      normalContrast: BLACK[900],
      lightForeground: ORANGE[900],
      lightBackground: ORANGE[900],
      lightContrast: BLACK[0],
      darkForeground: ORANGE[400],
      darkBackground: ORANGE[400],
      darkContrast: BLACK[900],
    },
    urgency: {
      normalForeground: BROWN[400],
      normalBackground: BROWN[500],
      normalContrast: BLACK[0],
      lightForeground: BROWN[900],
      lightBackground: BROWN[900],
      lightContrast: BLACK[0],
      darkForeground: BROWN[300],
      darkBackground: BROWN[300],
      darkContrast: BLACK[900],
    },
    critical: {
      normalForeground: RED[600],
      normalBackground: RED[600],
      normalContrast: BLACK[0],
      lightForeground: RED[900],
      lightBackground: RED[900],
      lightContrast: BLACK[0],
      darkForeground: RED[400],
      darkBackground: RED[400],
      darkContrast: BLACK[900],
    },
    favourite: {
      normalForeground: RED[600],
      normalBackground: RED[600],
      normalContrast: BLACK[0],
      lightForeground: RED[900],
      lightBackground: RED[900],
      lightContrast: BLACK[0],
      darkForeground: RED[400],
      darkBackground: RED[400],
      darkContrast: BLACK[900],
    },
  },
}

const highlightPalette: Record<App.Design.PaletteType, App.Design.HighlightPalette<string>> = {
  default: {
    ...legacyDefaultPalette.highlight,
    primary: {
      normalForeground: CORAL[300],
      normalBackground: CORAL[300],
      normalContrast: BLACK[900],
      normalActive: shade(0.24, CORAL[300]), // TODO: migrate to use overlay method
      normalHover: shade(0.16, CORAL[300]), // TODO: migrate to use overlay method
      lightForeground: CORAL[50],
      lightBackground: CORAL[50],
      lightContrast: BLACK[900],
      darkForeground: OCEAN[700],
      darkBackground: OCEAN[700],
      darkContrast: BLACK[0],
      darkActive: shade(0.24, OCEAN[700]), // TODO: migrate to use overlay method
    },
  },
  inverse: {
    ...legacyInversePalette.highlight,
    primary: {
      normalForeground: CORAL[300],
      normalBackground: CORAL[300],
      normalContrast: BLACK[900],
      normalActive: shade(0.24, CORAL[300]), // TODO: migrate to use overlay method
      normalHover: shade(0.16, CORAL[300]), // TODO: migrate to use overlay method
      lightForeground: CORAL[900],
      lightBackground: CORAL[900],
      lightContrast: BLACK[0],
      darkForeground: OCEAN[400],
      darkBackground: OCEAN[700],
      darkContrast: BLACK[0],
      darkActive: shade(0.24, OCEAN[700]), // TODO: migrate to use overlay method
    },
  },
}

export const luxuryEscapesRebrandDefaultPalette: App.Design.Palette<string> = {
  ...legacyDefaultPalette,
  neutral: neutralPalette.default,
  product: productPalette.default,
  messaging: messagingPalette.default,
  highlight: highlightPalette.default,
}

export const luxuryEscapesRebrandInversePalette: App.Design.Palette<string> = {
  ...legacyInversePalette,
  neutral: neutralPalette.inverse,
  product: productPalette.inverse,
  messaging: messagingPalette.inverse,
  highlight: highlightPalette.inverse,
}

export const luxuryEscapesRebrandShadows: App.Design.Shadows<string> = { ...baseThemeShadows }
export const luxuryEscapesRebrandBorderRadii: App.Design.BorderRadii<string> = { ...baseBorderRadii }

const rebrandHeadingMetrics = {
  one: { lineHeight: '40px', lineHeightTablet: '48px', iconSize: '40px' },
  oneAlt: { lineHeight: '40px', lineHeightTablet: '48px', iconSize: '40px' },
  two: { lineHeight: '32px', lineHeightTablet: '40px', iconSize: '40px' },
  twoAlt: { lineHeight: '32px', lineHeightTablet: '40px', iconSize: '40px' },
  three: { lineHeight: '26px', lineHeightTablet: '32px', iconSize: '32px' },
  threeAlt: { lineHeight: '26px', lineHeightTablet: '32px', iconSize: '32px' },
  four: { lineHeight: '21px', lineHeightTablet: '24px', iconSize: '24px' },
  five: { lineHeight: '22px', lineHeightTablet: '24px', iconSize: '20px' },
  six: { lineHeight: '19px', lineHeightTablet: '19px', iconSize: '16px' },
}

export const REBRAND_SUISSE_INTL_FONT = `"Suisse Int'l Rebrand", "Helvetica Neue", Helvetica, ${CJK_SANS_FONTS}, "Noto Sans Arabic", "Noto Sans Hebrew", Arial, sans-serif`
export const REBRAND_SUISSE_WORKS_FONT = `"Suisse Works Rebrand", "Suisse Int'l Rebrand", "Helvetica Neue", Helvetica, ${CJK_SERIF_FONTS}, "Noto Naskh Arabic", "Noto Sans Arabic", "Noto Sans Hebrew", Arial, sans-serif`

export const luxuryEscapesRebrandTheme: App.Design.Theme = {
  ...luxuryEscapesTheme,
  typography: {
    ...luxuryEscapesTheme.typography,
    heading: {
      ...luxuryEscapesTheme.typography.heading,
      one: {
        ...rebrandHeadingMetrics.one,
        fontFamily: REBRAND_SUISSE_WORKS_FONT,
        fontStyle: 'normal',
        fontWeight: '400',
        letterSpacing: '-0.5px',
      },
      oneAlt: {
        ...rebrandHeadingMetrics.oneAlt,
        fontFamily: REBRAND_SUISSE_WORKS_FONT,
        fontStyle: 'italic',
        fontWeight: '500',
        letterSpacing: '-0.5px',
      },
      two: {
        ...rebrandHeadingMetrics.two,
        fontFamily: REBRAND_SUISSE_WORKS_FONT,
        fontStyle: 'normal',
        fontWeight: '400',
        letterSpacing: '-0.5px',
      },
      twoAlt: {
        ...rebrandHeadingMetrics.twoAlt,
        fontFamily: REBRAND_SUISSE_WORKS_FONT,
        fontStyle: 'italic',
        fontWeight: '500',
        letterSpacing: '-0.5px',
      },
      three: {
        ...rebrandHeadingMetrics.three,
        fontFamily: REBRAND_SUISSE_WORKS_FONT,
        fontStyle: 'normal',
        fontWeight: '400',
        letterSpacing: '-0.5px',
      },
      threeAlt: {
        ...rebrandHeadingMetrics.threeAlt,
        fontFamily: REBRAND_SUISSE_WORKS_FONT,
        fontStyle: 'italic',
        fontWeight: '500',
        letterSpacing: '-0.5px',
      },
      four: {
        ...rebrandHeadingMetrics.four,
        fontFamily: REBRAND_SUISSE_INTL_FONT,
        fontStyle: 'normal',
        fontWeight: '600',
      },
      five: {
        ...rebrandHeadingMetrics.five,
        fontFamily: REBRAND_SUISSE_INTL_FONT,
        fontStyle: 'normal',
        fontWeight: '600',
      },
      six: {
        ...rebrandHeadingMetrics.six,
        fontFamily: REBRAND_SUISSE_INTL_FONT,
        fontStyle: 'normal',
        fontWeight: '600',
      },
    },
  },
  font: {
    ...luxuryEscapesTheme.font,
    primary: {
      ...luxuryEscapesTheme.font.primary,
      provider: 'local',
      localFileSrc: '/fonts/LE-fonts.css',
      name: 'Suisse Int\'l Rebrand',
      family: REBRAND_SUISSE_INTL_FONT,
      fontStyle: 'normal',
      weight: {
        regular: '400',
        medium: '500',
        bold: '500',
        superBold: '600',
      },
    },
    secondary: {
      ...luxuryEscapesTheme.font.secondary,
      provider: 'local',
      localFileSrc: '/fonts/LE-fonts.css',
      name: 'Suisse Int\'l Rebrand',
      family: REBRAND_SUISSE_INTL_FONT,
      fontStyle: 'normal',
      weight: {
        regular: '400',
        medium: '500',
        bold: '500',
        superBold: '600',
      },
    },
    secondaryAlternate: {
      ...luxuryEscapesTheme.font.secondaryAlternate,
      provider: 'local',
      localFileSrc: '/fonts/LE-fonts.css',
      name: 'Suisse Works Rebrand',
      family: REBRAND_SUISSE_WORKS_FONT,
      fontStyle: 'italic',
      weight: {
        regular: '400',
        medium: '500',
        bold: '500',
        superBold: '600',
      },
    },
  },
}

export default luxuryEscapesRebrandTheme
